import React, { useState, useMemo, useEffect, MouseEvent, useCallback } from 'react';
import {
    Button,
    Checkbox,
    FormControlLabel,
    Popover,
    Typography
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import './TagsFilter.scss';
import DebouncedInput from '../DebouncedInput';
import { useTranslation } from 'react-i18next';
import { Option } from '../';
import { MediaTag, TagsFilter as TagsFilterType } from '../types';
import debounce from 'lodash/debounce';

interface TagsFilterProps {
    data: any;
    mediaTags: MediaTag[];
    onChange: (_params: TagsFilterType) => void;
    removeFilter: (_param: string) => void;
}

const TagsFilter = React.memo(({ data, mediaTags, onChange, removeFilter }: TagsFilterProps) => {
    const { t: translator } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [searchString, setSearchString] = useState<string>('');
    const [checked, setChecked] = useState<string[]>(data?.tags || []);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchString('');
    };

    const toggleCheckbox = (option: MediaTag) => {
        setChecked(prevChecked => {
            const val = option.name;
            const newChecked = prevChecked.includes(val) ? prevChecked.filter(v => v !== val) : [...prevChecked, val];
            return newChecked
        });
    };

    const handleOptionsReset = () => {
        setChecked([]);
    };

    const open = Boolean(anchorEl);

    const filteredOptions = useMemo(
        () => mediaTags.filter((tag) => tag.name.toLowerCase().includes(searchString.toLowerCase())),
        [searchString, mediaTags]
    );

    const debouncedOnChange = useCallback(
        debounce((checkedTags: string[]) => {
            const query = checkedTags.length ? ` AND tags:(${checkedTags.map(tag => `"${tag}"`).join(' ')})` : '';
            onChange({ tags: checkedTags, query });
        }, 300),
        [onChange]
    );

    useEffect(() => {
        debouncedOnChange(checked);
        // Cancel on unmount to avoid memory leaks
        return () => {
            debouncedOnChange.cancel();
        };
    }, [checked]);

    const clearIcon = (
        <ClearIcon
            className="clear-search-icon"
            fontSize="small"
            color="disabled"
            onClick={() => {
                removeFilter(Option._TAGS);
            }}
        />
    );

    return (
        <div>
            <Button
                variant="contained"
                onClick={handleClick}
                className="menu-btn no-shadow"
                endIcon={clearIcon}
            >
                <b>{translator('COMMON_TAGS')}:&nbsp;</b>
                {`${Object.values(checked).filter(i => i).length} ${translator('COMMON_SELECTED')}`}
            </Button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'popover-content' }}
            >
                <DebouncedInput
                    id="search-options"
                    className="search-options"
                    placeholder={translator('COMMON_SEARCH')}
                    value={searchString}
                    onChange={setSearchString}
                    endIcon={<SearchIcon />}
                />

                <div className="checkbox-list">
                    {filteredOptions.length === 0 ? (
                        <div className="no-content">
                            <Typography variant="body2" color="textSecondary">
                                {translator('MODAL_CREATE_SEARCH_FILTER_NO_TAGS_FOUND')}
                            </Typography>
                        </div>
                    ) : (
                        filteredOptions.slice(0, 10).map((option, index) => (
                            <div
                                className="checkbox-row"
                                key={index}
                                onClick={(e) => {
                                    if (!(e.target as HTMLElement).closest('label')) {
                                        toggleCheckbox(option);
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={checked.includes(option.name)}
                                            onChange={() => toggleCheckbox(option)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    }
                                    label={option.name}
                                />
                            </div>
                        ))
                    )}
                </div>

                <Button
                    variant="contained"
                    className="footer-button"
                    onClick={handleOptionsReset}
                    disabled={Object.values(checked).every((v) => !v)}
                >
                    {translator('MODAL_CREATE_SEARCH_FILTER_CLEAR_SELECTION_BUTTON')}
                </Button>
            </Popover>
        </div>
    );
});

export default TagsFilter;
